import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MdSearch, MdPersonOutline } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { Skeleton } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import {
  instructorStudentsService,
  type InstructorStudent,
} from "services/instructorStudentsService";
import type { PaginatedMeta } from "services/apiTypes";
import { formatStudentSpecialty } from "lib/instructorStudentCourseFilter";
import StudentAvatar from "components/students/StudentAvatar";

function useDebounced<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatCourseDateRange(startDate?: string | null, endDate?: string | null) {
  return [startDate, endDate].filter(Boolean).join(" - ");
}

export default function StudentsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [searchParams] = useSearchParams();
  const courseIdParam = searchParams.get("course_id");
  const courseIdFilter = courseIdParam ? Number(courseIdParam) : null;

  const [term, setTerm] = React.useState("");
  const debouncedTerm = useDebounced(term, 350);

  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(20);

  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<InstructorStudent[]>([]);
  const [meta, setMeta] = React.useState<PaginatedMeta | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await instructorStudentsService.list({
        page,
        limit,
        term: debouncedTerm.trim() || undefined,
        course_id:
          courseIdFilter && Number.isFinite(courseIdFilter) ? courseIdFilter : undefined,
      });
      setItems(Array.isArray(res.data) ? res.data : []);
      setMeta(res.meta ?? null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedTerm, courseIdFilter]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  React.useEffect(() => {
    setPage(1);
  }, [debouncedTerm, courseIdFilter]);

  const totalPages = Math.max(1, meta?.last_page ?? 1);

  return (
    <div className="w-full space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "الطلاب" : "Students"}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isArabic
              ? "اعرض الطلاب المقبولين في كورساتك، مع الكورس الخاص بكل طالب."
              : "View students accepted into your courses and their enrolled courses."}
          </p>
        </div>
      </div>

      {courseIdFilter && Number.isFinite(courseIdFilter) ? (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-200">
          {isArabic
            ? "عرض الطلاب المشتركين في هذا الكورس فقط."
            : "Showing students enrolled in this course only."}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="relative">
          <span
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400 ${
              isArabic ? "right-3" : "left-3"
            }`}
          >
            <MdSearch className="h-5 w-5" />
          </span>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={isArabic ? "ابحث بالاسم..." : "Search by name..."}
            className={`w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
              isArabic ? "pl-4 pr-10 text-right" : "pl-10 pr-4 text-left"
            }`}
          />
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<MdPersonOutline className="h-6 w-6" />}
              title={isArabic ? "لا توجد نتائج" : "No students found"}
              description={
                isArabic
                  ? "جرب بحثًا مختلفًا."
                  : "Try a different search."
              }
            />
          ) : (
            <ul className="flex flex-col divide-y divide-gray-100 dark:divide-white/10">
              {items.map((s) => (
                <li key={s.id}>
                  <StudentRow
                    student={s}
                    isArabic={isArabic}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {meta && meta.total > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>
              {isArabic
                ? `عرض ${meta.from ?? 0}–${meta.to ?? 0} من ${meta.total}`
                : `Showing ${meta.from ?? 0}–${meta.to ?? 0} of ${meta.total}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
              >
                {isArabic ? "السابق" : "Prev"}
              </button>
              <span className="px-2 font-semibold text-gray-700 dark:text-gray-200">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
              >
                {isArabic ? "التالي" : "Next"}
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

function StudentRow({
  student,
  isArabic,
}: {
  student: InstructorStudent;
  isArabic: boolean;
}) {
  const canView = !!student.can_view;
  const specialty = formatStudentSpecialty(student.profile, isArabic);
  const courses = student.enrolled_courses ?? [];

  const content = (
    <div className="flex items-center gap-3 py-3">
      <StudentAvatar
        name={student.name}
        image={student.image}
        imageUrl={student.image_url}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-800 dark:text-white">
          {student.name ?? (isArabic ? "طالب" : "Student")}
        </p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
          {specialty || "—"}
        </p>
      </div>
      <div className="hidden min-w-0 max-w-md flex-1 flex-wrap justify-end gap-1 sm:flex">
        {courses.length > 0 ? (
          courses.map((course) => {
            const dateRange = formatCourseDateRange(course.start_date, course.end_date);
            return (
              <span
                key={course.id}
                className="max-w-44 rounded-xl bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                title={dateRange ? `${course.title} (${dateRange})` : course.title}
              >
                <span className="block truncate">{course.title}</span>
                {dateRange ? (
                  <span className="block truncate text-[10px] font-medium text-brand-500 dark:text-brand-300">
                    {dateRange}
                  </span>
                ) : null}
              </span>
            );
          })
        ) : (
          <span className="text-xs text-gray-400">
            {isArabic ? "غير مسجّل في كورساتك" : "Not enrolled in your courses"}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-between gap-2 rounded-xl px-3 transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
      {canView ? (
        <Link to={`${student.id}`} className="min-w-0 flex-1">
          {content}
        </Link>
      ) : (
        <div className="min-w-0 flex-1">{content}</div>
      )}
    </div>
  );
}
